/**
 * pendingThreadOpen — the combined-view "switch-on-open" handshake
 * (multi-org plan §10, 2026-08-16).
 *
 * Opening a foreign-workspace thread from the combined view NEVER renders it
 * in place (invariant 1: one workspace at a time). Instead the client saves
 * this marker, performs the server-verified workspace switch, and hard-
 * navigates (the established switch contract); after the reload, the chat
 * boot consumes the marker and activates the thread inside its own
 * workspace's portal.
 *
 * Mirrors the justJoinedOrg marker exactly: localStorage, short TTL, consume
 * clears. TTL keeps a crashed switch from re-activating a stale thread days
 * later.
 */

const KEY = 'prest0_pending_thread_open';
const TTL_MS = 10 * 60 * 1000;

export function savePendingThreadOpen(threadId: string): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ threadId, at: Date.now() }));
  } catch { /* storage unavailable — the switch still lands on /desktop */ }
}

export function consumePendingThreadOpen(): string | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    localStorage.removeItem(KEY);
    const parsed = JSON.parse(raw) as { threadId?: unknown; at?: unknown };
    if (typeof parsed.threadId !== 'string' || !parsed.threadId) return null;
    if (typeof parsed.at !== 'number' || Date.now() - parsed.at > TTL_MS) return null;
    return parsed.threadId;
  } catch {
    return null;
  }
}

export function clearPendingThreadOpen(): void {
  try { localStorage.removeItem(KEY); } catch { /* noop */ }
}
